import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';

import {
  getProductValidationAnalytics,
  type ProductValidationAnalyticsResponse,
} from '../../api/productValidationAnalyticsApi';

const BORDER = '1px solid #e2e8f0';
const TXT = '#172033';
const SUB = '#667085';
const BLUE = '#0b4f71';
const GOOD = '#2f7d4a';
const WARN = '#a06a0a';

function n(section: Record<string, unknown> | undefined, key: string): number {
  const value = Number(section?.[key]);
  return Number.isFinite(value) ? value : 0;
}

function nullableNumber(
  section: Record<string, unknown> | undefined,
  key: string,
): number | null {
  const raw = section?.[key];
  if (raw === null || raw === undefined || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function bool(
  section: Record<string, unknown> | undefined,
  key: string,
): boolean | null {
  const raw = section?.[key];
  if (raw === true || raw === false) return raw;
  if (raw === null || raw === undefined) return null;
  return String(raw).toLowerCase() === 'true';
}

function pct(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}%`;
}

function passChip(value: boolean | null) {
  if (value === true) {
    return { label: 'PASS', sx: { color: GOOD, bgcolor: '#edf8f1', border: '1px solid #b9ddc4' } };
  }
  if (value === false) {
    return { label: '미달', sx: { color: WARN, bgcolor: '#fff8e8', border: '1px solid #ead9a8' } };
  }
  return { label: '표본 대기', sx: { color: SUB, bgcolor: '#f8fafc', border: BORDER } };
}

export default function AdminProductValidationPanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<ProductValidationAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await getProductValidationAnalytics(14, 28));
    } catch (e: any) {
      setError(
        e?.response?.data?.message ??
          e?.response?.data?.detail ??
          '유료화 계측 데이터를 불러오지 못했습니다.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggle = useCallback(() => {
    setOpen((current) => {
      const next = !current;
      if (next && !data && !loading) void load();
      return next;
    });
  }, [data, load, loading]);

  const view = useMemo(() => {
    const summary = data?.summary;
    const retention = data?.retention;
    const quality = data?.quality;
    const gate = data?.gate;

    return {
      activeUsers: n(summary, 'activeBriefUsers'),
      activeTarget: n(gate, 'activeUserTarget'),
      activePass: bool(gate, 'activeUserPass'),
      return7d: nullableNumber(retention, 'returnWithin7dRatePct'),
      return7dTarget: nullableNumber(gate, 'returnWithin7dTargetPct'),
      return7dPass: bool(gate, 'returnWithin7dPass'),
      saveRate: nullableNumber(summary, 'watchlistSaveRatePct'),
      saveTarget: nullableNumber(gate, 'watchlistSaveTargetPct'),
      savePass: bool(gate, 'watchlistSavePass'),
      ready: bool(gate, 'readyForPaymentExperiment') === true,
      matured: n(retention, 'maturedCohortUsers'),
      duplicate: n(quality, 'duplicateBriefUserDayEvents'),
      orphan: n(quality, 'orphanBriefEvents'),
      invalidDate: n(quality, 'invalidKstDateEvents'),
      missingTicker: n(quality, 'watchlistSaveMissingTickerEvents'),
      loadErrors: n(quality, 'briefLoadErrorEvents'),
    };
  }, [data]);

  const metricCards = [
    {
      label: 'Brief 활성 사용자',
      value: `${view.activeUsers}명`,
      target: `목표 ${view.activeTarget || 30}명`,
      pass: view.activePass,
    },
    {
      label: '7일 이내 재방문',
      value: pct(view.return7d),
      target: `목표 ${pct(view.return7dTarget ?? 20)}`,
      pass: view.return7dPass,
    },
    {
      label: '관심종목 저장률',
      value: pct(view.saveRate),
      target: `목표 ${pct(view.saveTarget ?? 30)}`,
      pass: view.savePass,
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1.2,
        p: { xs: 1.05, md: 1.25 },
        borderRadius: 2.4,
        bgcolor: '#fbfcfe',
        border: BORDER,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={0.8}
      >
        <Box>
          <Typography sx={{ color: '#245da8', fontSize: 9, fontWeight: 950, letterSpacing: '.08em' }}>
            PAID VALUE VALIDATION
          </Typography>
          <Typography sx={{ mt: 0.15, color: TXT, fontSize: 13.5, fontWeight: 950 }}>
            유료화 계측
          </Typography>
          <Typography sx={{ mt: 0.2, color: SUB, fontSize: 10.7, lineHeight: 1.5 }}>
            실제 비ADMIN 사용자의 Brief 사용·재방문·관심종목 저장을 확인합니다.
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.6}>
          {open ? (
            <Button
              size="small"
              startIcon={<RefreshRoundedIcon />}
              disabled={loading}
              onClick={() => void load()}
              sx={{ fontWeight: 900, textTransform: 'none' }}
            >
              새로고침
            </Button>
          ) : null}
          <Button
            variant={open ? 'outlined' : 'contained'}
            size="small"
            startIcon={<WorkspacePremiumRoundedIcon />}
            onClick={handleToggle}
            sx={{
              minHeight: 36,
              borderRadius: 1.8,
              bgcolor: open ? undefined : BLUE,
              fontWeight: 950,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: open ? undefined : '#083e5a', boxShadow: 'none' },
            }}
          >
            {open ? '계측 닫기' : '유료화 계측 보기'}
          </Button>
        </Stack>
      </Stack>

      {open ? (
        <Box sx={{ mt: 1 }}>
          {loading ? <LinearProgress sx={{ mb: 0.9 }} /> : null}

          {error ? (
            <Box sx={{ p: 0.9, borderRadius: 1.7, bgcolor: '#fff6f6', border: '1px solid #f0cccc' }}>
              <Typography sx={{ color: '#b42318', fontSize: 10.8, fontWeight: 850 }}>
                {error}
              </Typography>
            </Box>
          ) : null}

          {data ? (
            <>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={0.65}
                sx={{ mt: error ? 0.8 : 0 }}
              >
                {metricCards.map((item) => {
                  const chip = passChip(item.pass);
                  return (
                    <Box
                      key={item.label}
                      sx={{
                        flex: 1,
                        p: 0.9,
                        borderRadius: 1.8,
                        bgcolor: '#fff',
                        border: BORDER,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.5}>
                        <Typography sx={{ color: SUB, fontSize: 9.5, fontWeight: 850 }}>{item.label}</Typography>
                        <Chip
                          size="small"
                          label={chip.label}
                          sx={{ height: 21, fontSize: 9, fontWeight: 950, ...chip.sx }}
                        />
                      </Stack>
                      <Typography sx={{ mt: 0.35, color: TXT, fontSize: 20, fontWeight: 950 }}>
                        {item.value}
                      </Typography>
                      <Typography sx={{ mt: 0.12, color: '#8793a2', fontSize: 9.4 }}>
                        {item.target}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>

              <Box
                sx={{
                  mt: 0.75,
                  p: 0.9,
                  borderRadius: 1.8,
                  bgcolor: view.ready ? '#edf8f1' : '#f8fafc',
                  border: view.ready ? '1px solid #b9ddc4' : BORDER,
                }}
              >
                <Typography sx={{ color: view.ready ? GOOD : TXT, fontSize: 11.5, fontWeight: 950 }}>
                  {view.ready ? '결제 실험 Gate 충족' : '결제 실험 Gate 관찰 중'}
                </Typography>
                <Typography sx={{ mt: 0.2, color: SUB, fontSize: 9.9, lineHeight: 1.5 }}>
                  측정 버전 {data.measurementVersion ?? '—'} · 최근 {data.days ?? 14}일 ·
                  7일 retention 성숙 cohort {view.matured}명
                </Typography>
              </Box>

              <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 0.45 }}>
                {[
                  ['중복 Brief', view.duplicate],
                  ['user 없는 Brief', view.orphan],
                  ['잘못된 KST 날짜', view.invalidDate],
                  ['ticker 누락 저장', view.missingTicker],
                  ['Brief load 오류', view.loadErrors],
                ].map(([label, value]) => (
                  <Box key={String(label)} sx={{ p: 0.65, borderRadius: 1.5, bgcolor: '#fff', border: BORDER }}>
                    <Typography sx={{ color: '#8793a2', fontSize: 8.5, fontWeight: 850 }}>{label}</Typography>
                    <Typography sx={{ mt: 0.1, color: TXT, fontSize: 12, fontWeight: 950 }}>{String(value)}</Typography>
                  </Box>
                ))}
              </Box>

              <Typography sx={{ mt: 0.7, color: '#8793a2', fontSize: 9.2, lineHeight: 1.5 }}>
                ADMIN 행동은 canonical KPI에서 제외됩니다. Gate는 결제 실험 진입을 위한 운영 기준이며 자동 결제 활성화 조건이 아닙니다.
              </Typography>
            </>
          ) : null}
        </Box>
      ) : null}
    </Paper>
  );
}
